import React, { useMemo, useState } from 'react';
import {
  Button,
  InputNumber,
  Modal,
  Space,
  TablePaginationConfig,
  TableProps,
  Tooltip,
} from 'antd';
import TableComponent from '../table';
import { IStock } from '../../interfaces/stock';
import { useQuery } from '@tanstack/react-query';
import Search from 'antd/es/input/Search';
import { ISearchStockReq } from '@/interfaces/stock';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { AxiosResponse } from 'axios';
import { queryPriceSalesStock } from '@/adapters/stock';
import WarningConfirmModal from '../modal/confirm-warning';
import { IDataModal } from '@/interfaces/dataModal';
import dayjs from 'dayjs';
import { twMerge } from 'tailwind-merge';
import { ExclamationCircleOutlined } from '@ant-design/icons';

interface IProps {
  open: boolean;
  handleOk: (items: IStock[]) => void;
  handleCancel: () => void;
  isMultiSelect?: boolean;
  exists?: number[];
  customerId: number;
}

const _criteria: ISearchStockReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
  isActive: true,
};
const StockSelectorModal = ({
  open,
  handleOk,
  handleCancel,
  isMultiSelect = false,
  exists,
  customerId,
}: IProps) => {
  const [criteria, setCriteria] = useState<ISearchStockReq>({
    ..._criteria,
  });

  const _modal: IDataModal<IStock> = {
    open: false,
  };

  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [selected, setSelected] = useState<IStock | null>(null);
  const [confirm, setConfirm] = useState<IDataModal<IStock>>(_modal);
  const { data, isPending } = useQuery<AxiosResponse<IStock[]>, Error>({
    queryKey: ['search-stock', customerId],
    queryFn: async () => await queryPriceSalesStock(customerId),
    enabled: isOpened,
    // enabled: open && !!customerId,
  });

  const onTableChange = (pagination: TablePaginationConfig) => {
    const newCriteria = { ...criteria };
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      orderBy: 'id',
      orderType: '',
      pageStart: 1,
      pageSize: 10,
    });
  };

  const columns: TableProps<IStock>['columns'] = [
    {
      title: 'LOT No.',
      dataIndex: 'code',
      key: 'code',
      className: 'font-bold',
      width: 120,
      align: 'center',
      fixed: 'left',
    },
    {
      title: 'Expire Date',
      dataIndex: 'coaExpiredDate',
      key: 'coaExpiredDate',
      width: 120,
      align: 'center',
      className: 'font-bold',
      render: _ => {
        const now = dayjs();
        const date = dayjs(_);
        const daysUntilExpire = date.diff(now, 'day'); // Number of days until expiration
        const isExpiringSoon = date.isBefore(now.add(6, 'month'));

        return (
          <>
            {_ && (
              <Space className={twMerge(isExpiringSoon && 'text-red-600')}>
                {formatDate(_)}
              </Space>
            )}
            {isExpiringSoon && (
              <div className="text-xs">
                ({daysUntilExpire || ''} day
                {daysUntilExpire !== 1 ? 's' : ''} left)
              </div>
            )}
          </>
        );
      },
      sorter: (a, b) =>
        dayjs(a.coaExpiredDate).unix() - dayjs(b.coaExpiredDate).unix(),
    },
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      key: 'productCode',
      className: 'font-bold',
      width: 130,
      align: 'center',
    },
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'productName',
      key: 'productName',
      className: 'font-bold',
    },
    {
      title: 'ราคา',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      className: 'font-bold text-blue-600',
      align: 'right',
      width: 100,
      sorter: (a, b) => Number(a.unitPrice) - Number(b.unitPrice),
      render: (_, rec) => {
        const isExpired =
          rec.validTo && dayjs(rec.validTo).isBefore(dayjs(), 'day');
        return (
          <div
            className={twMerge(
              'flex gap-1 justify-end items-center',
              isExpired && 'text-red-500',
            )}
          >
            {formatNumber(_)}
            {isExpired && (
              <Tooltip
                placement="top"
                title={`Expired ${rec.validTo && formatDate(rec.validTo)}`}
              >
                <ExclamationCircleOutlined />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      sorter: false,
      title: 'จำนวน',
      dataIndex: 'packQuantity',
      key: 'packQuantity',
      className: 'font-bold text-gray-600',
      align: 'right',
      width: 60,
      render: (_, rec) => (
        <Space>{formatNumber(rec.quantity / (rec.conversion || 1))}</Space>
      ),
    },
    {
      title: 'Package',
      dataIndex: 'packageUnitName',
      key: 'packageUnitName',
      className: 'font-bold',
      width: 80,
    },
    {
      title: 'จอง',
      dataIndex: 'reservedQuantity',
      key: 'reservedQuantity',
      className: 'font-bold text-orange-600',
      align: 'right',
      width: 100,
      sorter: (a, b) => Number(a.reservedQuantity) - Number(b.reservedQuantity),
      render: _ => formatNumber(_ || 0),
    },
    {
      title: 'คงเหลือ',
      dataIndex: 'remain',
      key: 'remain',
      className: 'font-bold text-green-600',
      align: 'right',
      width: 100,
      sorter: (a, b) => Number(a.remain) - Number(b.remain),
      render: _ => formatNumber(_),
    },
    {
      title: 'หน่วยขาย',
      dataIndex: 'baseUnitName',
      key: 'baseUnitName',
      fixed: 'left',
      className: 'font-bold',
      width: 80,
    },
    {
      title: 'จำนวน',
      dataIndex: 'quantity',
      key: 'quantity',
      className: 'font-bold text-blue-600',
      align: 'right',
      width: 120,
      render: (_, rec) => (
        <InputNumber
          type="number"
          placeholder="จำนวน"
          className="w-full"
          min={1}
          step={rec.conversion}
          onChange={v =>
            setSelected({
              ...rec,
              quantity: v as number,
            })
          }
        />
      ),
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      className: 'font-medium',
      align: 'center',
      fixed: 'right',
      width: 60,
      hidden: isMultiSelect,
      render: (_, rec: IStock) => (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            if (
              Number(selected?.quantity) >
              Number(rec.remain || 0) - Number(rec.reservedQuantity || 0)
            ) {
              setConfirm({
                open: true,
                item: selected as IStock,
              });
            } else {
              handleOk([selected as IStock]);
            }
          }}
          disabled={selected?.id !== rec.id}
        >
          เลือก
        </Button>
      ),
    },
  ];

  const stocks: IStock[] = data?.data || [];
  const filteredProducts = useMemo(() => {
    const searchLower = criteria?.search?.toLowerCase();

    if (!searchLower) {
      return stocks.filter(stock => !exists?.includes(stock.id));
    }

    const existsSet = new Set(exists);
    const filterStocks = stocks.filter(stock => {
      const matchesSearch =
        stock.code.toLowerCase().includes(searchLower) ||
        stock.productCode.toLowerCase().includes(searchLower) ||
        stock.productName.toLowerCase().includes(searchLower);

      return matchesSearch && !existsSet.has(stock.id);
    });

    return filterStocks;
  }, [stocks, criteria.search, exists]);

  return (
    <Modal
      title="เลือกสินค้า"
      open={open}
      width={'1250px'}
      onOk={() => handleOk}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      footer={[
        <Button key="back" onClick={handleCancel}>
          ปิด
        </Button>,
      ]}
      afterClose={() => {
        setSelected(null);
      }}
      afterOpenChange={status => setIsOpened(status)}
    >
      <div className="flex flex-col gap-2 w-full">
        <Search
          className="md:max-w-[400px] md:min-w-[400px]"
          placeholder="ค้นหา"
          allowClear
          size="large"
          enterButton="ค้นหา"
          onSearch={onSearch}
        />
        <TableComponent<IStock>
          columns={columns}
          criteria={criteria}
          data={{ data: filteredProducts || [], totalRecords: 0 }}
          loading={isPending}
          onTableChange={onTableChange}
          scrollLength={1200}
        />
        <WarningConfirmModal
          open={confirm?.open}
          title={'สินค้าไม่คงเหลือไม่เพียงต่อ คุณต้องการเพิ่มสินค้าใช่หรือไม่'}
          content={
            'สินค้าไม่คงเหลือไม่เพียงต่อ คุณต้องการเพิ่มสินค้าใช่หรือไม่'
          }
          handleOk={function (): void {
            handleOk([confirm.item as IStock]);
            setConfirm({ ..._modal });
          }}
          handleCancel={function (): void {
            setConfirm({ ..._modal });
          }}
        />
      </div>
    </Modal>
  );
};

export default StockSelectorModal;
